import * as React from "react"
import {Slot} from "@radix-ui/react-slot"
import {cva, type VariantProps} from "class-variance-authority"
import {cn} from "~/lib/utils"
import {Icon} from "../Icon/Icon"
import {DeckIcon} from "../DeckIcon/DeckIcon"
import {hasIcon} from "~/design-system/icons/icon-registry"
import {DotmSquare11} from "~/components/ui/dotm-square-11"
import styles from "./Button.module.css"

const buttonVariants = cva(
  styles.button_base,
  {
    variants: {
      variant: {
        default: styles.button_primary,
        secondary: styles.button_secondary,
        destructive: styles.button_destructive,
        outline: styles.button_outline,
        ghost: styles.button_ghost,
        ghostMuted: styles.button_ghost_muted,
        ghostInverted: styles.button_ghost_inverted,
        link: styles.button_link,
        linkInverted: styles.button_link_inverted,
        soft: styles.button_soft,
        brand: styles.button_brand,
        success: styles.button_success,
        warning: styles.button_warning,
        upgrade: styles.button_upgrade,
        ai: styles.button_ai,
      },
      size: {
        default: styles.button_size_default,
        sm: styles.button_size_sm,
        lg: styles.button_size_lg,
        icon: styles.button_size_icon,
        xs: styles.button_size_xs,
        'icon-xs': styles.button_size_icon_xs,
        'icon-sm': styles.button_size_icon_sm,
        'icon-lg': styles.button_size_icon_lg,
        '2xs': styles.button_size_2xs,
        'icon-2xs': styles.button_size_icon_2xs,
      },
    },
    defaultVariants: {
      variant: "default",
      size: "default",
    },
  }
)

export interface ButtonProps
  extends React.ButtonHTMLAttributes<HTMLButtonElement>,
    VariantProps<typeof buttonVariants> {
  asChild?: boolean
  loading?: boolean
  /**
   * PRESENTATIONAL progress, as opposed to `loading`.
   *
   * `loading` means the button is unavailable: it disables itself, swaps the whole content for a dot-matrix
   * plus label, and reserves the idle width. Correct when the press must not be repeated.
   *
   * `busy` means work is running that the button STARTED but does not own — several can run at once, the
   * control stays pressable, and the layout must not move. So it swaps only the lead icon, keeps the label
   * and does not disable. Use it when two of these sitting side by side should both be able to show
   * progress without taking each other away.
   */
  busy?: boolean
  leadIcon?: string | React.ReactNode
  tailIcon?: string | React.ReactNode
  badge?: React.ReactNode
  isLoading?: boolean
}

const iconColorMap = {
  default: styles.icon_white,
  secondary: styles.icon_default_muted,
  destructive: styles.icon_white,
  outline: styles.icon_default_muted,
  ghost: styles.icon_default_subtle,
  ghostMuted: styles.icon_muted,
  ghostInverted: styles.icon_white,
  link: styles.icon_default_muted,
  linkInverted: styles.icon_white,
  soft: styles.icon_default_muted,
  brand: styles.icon_default_muted,
  success: styles.icon_white,
  warning: styles.icon_white,
  upgrade: styles.icon_white,
  ai: styles.icon_default_muted,
}

// Layout-effect that no-ops on the server (Remix SSR) instead of warning — the width snapshot below is a
// DOM measurement, so it only ever matters after hydration anyway.
const useIsomorphicLayoutEffect = typeof window !== "undefined" ? React.useLayoutEffect : React.useEffect

const Button = React.forwardRef<HTMLButtonElement, ButtonProps>(
  ({ className, variant, size = "default", asChild = false, loading = false, busy = false, disabled, children, leadIcon, tailIcon, badge, isLoading, style, ...props }, ref) => {
    const Comp = asChild ? Slot : "button"

    // Hold the button at its resting width while loading so the (usually shorter) loading label can't
    // shrink it. The caller swaps `children` for the loading state — e.g. "Generate" → "Generating…" — so
    // we can't reserve the resting label in CSS; instead we snapshot the rendered idle width and pin it as
    // a `min-width` floor for the loading render. Cleared back to undefined once idle, so the button sizes
    // to its content normally the rest of the time.
    const innerRef = React.useRef<HTMLButtonElement | null>(null)
    const idleWidthRef = React.useRef<number | undefined>(undefined)
    const [reservedWidth, setReservedWidth] = React.useState<number | undefined>(undefined)

    const setRefs = React.useCallback(
      (node: HTMLButtonElement | null) => {
        innerRef.current = node
        if (typeof ref === "function") ref(node)
        else if (ref) (ref as React.MutableRefObject<HTMLButtonElement | null>).current = node
      },
      [ref],
    )

    useIsomorphicLayoutEffect(() => {
      // Re-measure only while idle (and not mid-measure), capturing the natural width for the current
      // resting content. Runs before paint, so freezing it below never flashes a resized button.
      if (!loading && innerRef.current) {
        idleWidthRef.current = innerRef.current.getBoundingClientRect().width
      }
    }, [loading, children, leadIcon, tailIcon, badge, size, variant, className])

    useIsomorphicLayoutEffect(() => {
      setReservedWidth(loading ? idleWidthRef.current : undefined)
    }, [loading])

    const renderIcon = (icon: string | React.ReactNode) => {
      if (typeof icon === 'string') {
        // Check if it's a DeckIcon, otherwise use RemixIcon
        const isDeckIcon = hasIcon(icon);
        const iconSize = size === 'lg' || size === 'icon-sm' || size === 'icon-lg' ? 'button-lg' : size === 'xs' || size === 'icon-xs' ? 'button-xs' : size === '2xs' || size === 'icon-2xs' ? 'button-2xs' : 'button-default';
        const iconClassName = cn(iconColorMap[variant || 'default'], {
          [styles.icon_spin]: isLoading,
        });
        
        if (isDeckIcon) {
          return <DeckIcon 
            name={icon} 
            size={iconSize}
            className={iconClassName}
          />;
        }
        
        return <Icon 
          name={icon} 
          size={iconSize}
          className={iconClassName}
        />;
      }
      return icon;
    };

    return (
      <Comp
        className={cn(buttonVariants({ variant, size, className }))}
        ref={setRefs}
        disabled={disabled || loading}
        data-loading={loading}
        data-busy={busy}
        style={reservedWidth != null ? { ...style, minWidth: reservedWidth } : style}
        {...props}
      >
        {loading ? (
          <div className={styles.loading_container}>
            <DotmSquare11 size={16} dotSize={2} ariaLabel="Loading" className={styles.loading_dotmatrix} />
            {children ? <span>{children}</span> : null}
          </div>
        ) : (
          <div className={styles.button_content}>
            {/* The dot-matrix takes the lead icon's own 16px slot, so a button that starts working keeps
                exactly the width it had — see `busy` above. */}
            {(leadIcon || busy) && (
              <span className={styles.button_lead_icon}>
                {busy ? (
                  <DotmSquare11 size={16} dotSize={2} ariaLabel="Working" className={styles.loading_dotmatrix} />
                ) : (
                  renderIcon(leadIcon)
                )}
              </span>
            )}
            <span className={styles.button_text}>{children}</span>
            {badge && <span className={styles.button_badge}>{badge}</span>}
            {tailIcon && <span className={styles.button_tail_icon}>{renderIcon(tailIcon)}</span>}
          </div>
        )}
      </Comp>
    )
  }
)
Button.displayName = "Button"

export { Button, buttonVariants } 